import {compose, concat, includes, keys, map, omit, without} from 'ramda';
import {trainApi} from './trainApiImplementation.ts';
import {
  TrainApiAvailableDatesRoute,
  TrainApiOrganizationRoute,
  TrainApiRailwayLinesRoute,
  TrainApiServiceLinesRoute,
  TrainApiTrainFormationsRoute,
  TrainApiTrainRouteGroupsRoute,
  TrainApiTrainRoutesRoute,
  TrainApiTrainRunsRoute,
} from 'types/apis/trainApi';

import {ServiceLine} from 'types/trainRouteGroups/serviceLine';
import {RailwayLine, RailwayLineResponse} from 'types/railways/railwayLine';
import {TrainRoute, TrainRouteResponse} from 'types/trainRouteGroups/trainRoute';
import {
  TrainRouteGroup,
  TrainRouteGroupResponse,
} from 'types/trainRouteGroups/trainRouteGroup';
import {
  TrainFormation,
  TrainFormationResponse,
  TrainFormationRunCount,
  TrainFormationRunCountResponse,
} from 'types/trains/trainFormation';
import {TrainRun, TrainRunResponse} from 'types/trainRuns/trainRun';
import {cemitApiGetUrl} from '../apiConfig.ts';
import {CemitTypename} from 'types/cemitTypename.ts';
import {createTrainRunFromResponse} from 'classes/typeCrud/trainRunCrud.ts';
import {createTrainGroupSingleTrainRunFromTrainRunResponse} from 'classes/typeCrud/trainGroupCrud.ts';
import {dateIntervalResponsesToDateIntervals} from 'utils/datetime/dateUtils.ts';
import {DateIntervalResponseData} from 'types/dateIntervalResponseData';
import {
  createTrainRoute,
  createTrainRouteMinimized,
} from 'classes/typeCrud/trainRouteCrud.ts';
import {createTrainRouteGroup} from 'classes/typeCrud/trainRouteGroupCrud.ts';
import {
  OrganizationLoaded,
  OrganizationMinimized,
  OrganizationResponse,
} from 'types/organizations/organization.ts';
import {FrontendView} from 'config/appConfigs/cemitAppConfigs/frontendConfig/frontendView.ts';

import {DataVisualizationOption} from 'types/organizations/dataVisualizationOption.ts';
import {TrainGroupOnlyTrainFormation} from 'types/trainGroups/trainGroupOnlyTrainFormation';
import {Error} from '@mui/icons-material';
import {createTrainFormation} from 'classes/typeCrud/trainFormationCrud.ts';
import {Perhaps} from 'types/typeHelpers/perhaps';
import {TrainGroupSingleTrainRun} from 'types/trainGroups/trainGroupSingleTrainRun';
import {clsOrTypes} from 'appUtils/typeUtils/clsOrTypes.ts';
import {
  createTrainGroupOnlyTrainFormationFromTrainFormation,
  trainFormationResponseToTrainGroupOnlyTrainFormation,
} from 'classes/typeCrud/trainGroupOnlyTrainFormationCrud.ts';
import {TrainGroup} from 'types/trainGroups/trainGroup';
import {DateInterval} from 'types/propTypes/trainPropTypes/dateInterval';
import {implementsCemitTypeViaClass} from 'classes/cemitAppCemitedClasses/cemitClassResolvers.ts';
import {clsOrType} from 'appUtils/typeUtils/clsOrType.ts';
import {
  CemitApiRequest,
  CemitApiResponseResolver,
  CemitApiTrainRunsRequest,
  CemitApiUrlResolver,
} from 'types/apis/cemitApi';

/**
 * Resolves a Organization instance from the API
 * @param organization
 */
export const trainApiOrganizationsResponseResolver: CemitApiResponseResolver<
  TrainApiOrganizationRoute,
  OrganizationLoaded,
  OrganizationResponse
> = ({data: organizations}: {data: OrganizationResponse[]}): OrganizationLoaded[] => {
  return clsOrTypes<OrganizationLoaded>(
    CemitTypename.organization,
    map((organization: OrganizationResponse) => {
      const views = concat(
        // Removed the disabled from all possibley views
        without(organization.frontendOptions.disabledViews || [], keys(FrontendView)),
        // Special views of the Organization, usually experimental or custom applications/trafficSimComponents
        organization.frontendOptions.views || [],
      );
      return {
        ...omit(['frontendOptions'], organization),
        frontendOptions: {
          ...omit(['views', 'disabledViews'], organization.frontendOptions),
          views,
          // TOOD temporarily hard coding trainRoute organizations to rmse and speed
          // and wheelscan organizations to timeSeries
          dataVisualizationOptions: includes(FrontendView.trainRoute, views)
            ? [DataVisualizationOption.rmse, DataVisualizationOption.speed]
            : [DataVisualizationOption.timeSeries],
        },
      } as OrganizationLoaded;
    }, organizations),
  );
};

/**
 * Resolves a Organization instance from the API
 * @param organization
 */
export const trainApiOrganizationsAdminOnlyResponseResolver: CemitApiResponseResolver<
  TrainApiOrganizationRoute,
  OrganizationLoaded,
  OrganizationResponse
> = ({data: organizations}: {data: OrganizationResponse[]}): OrganizationMinimized[] => {
  return clsOrTypes<OrganizationMinimized>(
    CemitTypename.organizationMinimized,
    map((organization: OrganizationResponse) => {
      return organization as OrganizationMinimized;
    }, organizations),
  );
};

/**
 * Resolves TrainRun instances from the API, with or without SensorDataPoints
 * @param availableDateStrings
 */
export const trainApiTrainRunsResponseResolver: CemitApiResponseResolver<
  TrainApiTrainRunsRoute
> = ({
  data: trainRuns,
  error,
  request,
}: {
  data: TrainRunResponse[];
  error: Error;
  request: CemitApiRequest<TrainApiTrainRunsRequestProps, TrainApiTrainRunRequestParams>;
}): TrainGroup[] => {
  if (
    request.path == 'withSensorDataPoints' ||
    request.path == 'withTrainFormationSensorDataPoints'
  ) {
    return trainApiTrainGroupWithSensorDataPointsResponseResolver({
      data: trainRuns,
      error,
      request,
    });
  } else {
    // TrainRun response
    // TODO We currently only support TrainGroupSingleTrainRun, not TrainGroup.
    // We don't yet support multiple TrainRuns per TrainGroup
    return clsOrTypes<TrainGroupSingleTrainRun>(
      CemitTypename.trainGroupSingleTrainRun,
      map((trainRunResponse: TrainRunResponse): TrainGroup => {
        // Create a TrainGroup with a single TrainRun
        return compose<[TrainRunResponse], TrainRun, TrainGroup>(
          (trainRunResponse: TrainRunResponse) => {
            return createTrainGroupSingleTrainRunFromTrainRunResponse(trainRunResponse);
          },
          (trainRunResponse: TrainRunResponse) => {
            return createTrainRunFromResponse(
              {isPreconfigured: request.props?.isPreconfigured || false},
              trainRunResponse,
            );
          },
        )(trainRunResponse);
      }, trainRuns),
    );
  }
};

/**
 * Handle Detail queryies that return SensorDataPoints
 * @param trainRunResponseProps
 * @param error
 * @param request
 */
export const trainApiTrainGroupWithSensorDataPointsResponseResolver: CemitApiResponseResolver<
  TrainApiTrainRunsRoute
> = ({
  data: trainRunResponseProps,
  error,
  request,
}: {
  data: TrainRunResponse[];
  error: Error;
  request: CemitApiTrainRunsRequest;
}): TrainGroup[] => {
  // TODO Hack. We currently query for TrainFormation sensor data here as well as TrainRuns
  // The interface is unified by TrainGroup on the frontend but not yet on the backend, which
  // does not have a concept of TrainGroup. Thus we instantiate TrainGroupOnlyTrainFormation
  // if request
  if (
    implementsCemitTypeViaClass(
      CemitTypename.trainGroupOnlyTrainFormation,
      request.props.sensorDataTrainGroup!.trainGroup,
    )
  ) {
    // TrainFormations to TrainGroupOnlyTrainFormations
    return clsOrTypes<TrainGroupOnlyTrainFormation>(
      CemitTypename.trainGroupOnlyTrainFormation,
      map((trainRunResponse: TrainRunResponse): TrainGroupOnlyTrainFormation => {
        // Create a TrainGroupOnlyTrainFormation
        return trainFormationResponseToTrainGroupOnlyTrainFormation(trainRunResponse);
      }, trainRunResponseProps),
    );
  } else if (
    implementsCemitTypeViaClass(
      CemitTypename.trainGroupSingleTrainRun,
      request.props.sensorDataTrainGroup!.trainGroup,
    )
  ) {
    const trainGroupSingleTrainRuns: TrainGroupSingleTrainRun[] = map(
      (trainRunResponse: TrainRunResponse): TrainGroupSingleTrainRun => {
        // TrainRuns to TrainGroupSingleTrainRuns
        return clsOrType<TrainGroupSingleTrainRun>(
          CemitTypename.trainGroupSingleTrainRun,
          {
            ...request.props.sensorDataTrainGroup!.trainGroup,
            sensorDataPoints: trainRunResponse.sensorDataPoints,
          },
        );
      },
      trainRunResponseProps,
    );
    return trainGroupSingleTrainRuns;
  } else {
    throw new Error(
      `Unexpected type ${request.props.sensorDataTrainGroup.trainGroup.__typename}`,
    );
  }
};

/**
 * Queries for the available dates of TrainRuns matching the given TrainRoute or TrainRouteGroup
 * @param trainApiRoute
 * @returns {String} The trains-api Url
 */
export const trainApiAvailableDatesUrl: CemitApiUrlResolver<
  TrainApiAvailableDatesRoute
> = (trainApiRoute: TrainApiAvailableDatesRoute): Perhaps<string> => {
  // Get the available datetime ranges for a TrainRouteOrGroup or for a TrainFormation
  return cemitApiGetUrl(trainApi, trainApiRoute);
};

/**
 * Resolves Date Interval instances from the API
 * @param availableDateStrings
 */
export const trainApiAvailableDatesResponseResolver: CemitApiResponseResolver<
  TrainApiTrainRoutesRoute,
  DateInterval[]
> = ({
  data: dateIntervalResponses,
}: {
  data: DateIntervalResponseData[];
}): DateInterval[] => {
  return dateIntervalResponsesToDateIntervals(dateIntervalResponses);
};
/**
 * Resolves ServiceLine instances from the API
 * @param serviceLines
 */
export const trainApiServiceLinesResponseResolver: CemitApiResponseResolver<
  TrainApiServiceLinesRoute,
  ServiceLine[]
> = ({data: serviceLines}: {data: any[]}): ServiceLine[] => {
  return serviceLines as ServiceLine[];
};

/**
 * Resolves RailwayLine instances from the API
 * @param railwayLines
 * @param request
 */
export const trainApiRailwayLinesResponseResolver: CemitApiResponseResolver<
  TrainApiRailwayLinesRoute,
  RailwayLine[],
  RailwayLineResponse[]
> = ({
  data: railwayLines,
  request,
}: CemitApiResponseResolverProps<TrainApiRailwayLinesRoute>): RailwayLine[] => {
  // Handle the detail or root request
  const cemitTypeName =
    request.path == 'detail'
      ? CemitTypename.railwayLine
      : CemitTypename.railwayLineMinimized;
  return clsOrTypes<RailwayLine>(cemitTypeName, railwayLines);
};

/**
 * Resolves minimized TrainRoutes
 * @param trainRoutes
 */
export const trainApiMinimizedTrainRoutesResponseResolver: CemitApiResponseResolver<
  TrainApiTrainRoutesRoute,
  TrainRouteResponse[]
> = ({data: trainRoutes}: {data: TrainRouteResponse[]}): TrainRoute[] => {
  return map<TrainRouteResponse, TrainRoute>(
    (trainRouteResponse: TrainRouteResponse): TrainRoute => {
      return createTrainRouteMinimized(trainRouteResponse);
    },
    trainRoutes,
  );
};

export const trainApiDetailsTrainRoutesResponseResolver: CemitApiResponseResolver<
  TrainApiTrainRoutesRoute,
  TrainRouteResponse[]
> = ({data: trainRoutes}: {data: TrainRouteResponse[]}): TrainRoute[] => {
  return clsOrTypes<TrainRoute>(
    CemitTypename.trainRoute,
    map<TrainRouteResponse, TrainRoute>(
      (trainRouteResponse: TrainRouteResponse): TrainRoute => {
        return createTrainRoute(trainRouteResponse);
      },
      trainRoutes,
    ),
  );
};

/**
 *
 * @param trainRouteGroups
 */
export const trainApiTrainRouteGroupsResponseResolver: CemitApiResponseResolver<
  TrainApiTrainRouteGroupsRoute,
  TrainRouteGroup[]
> = ({data: trainRouteGroups}: {data: TrainRouteGroupResponse[]}): TrainRouteGroup[] => {
  return clsOrTypes<TrainRouteGroup>(
    CemitTypename.trainRouteGroup,
    map<TrainRouteGroupResponse, TrainRouteGroup>(
      (trainRouteGroupResponse: TrainRouteGroupResponse) => {
        return createTrainRouteGroup(trainRouteGroupResponse);
      },
      trainRouteGroups,
    ),
  );
};

/**
 * TrainFormations are always wrapped in a TrainGroup that has no specific TrainRuns
 * @param trainFormations
 */
export const trainApiTrainFormationsResponseResolver: CemitApiResponseResolver<
  TrainApiTrainFormationsRoute,
  TrainFormation[]
> = ({
  data: _trainFormations,
}: {
  data: TrainFormationResponse[];
}): TrainGroupOnlyTrainFormation[] => {
  return map(
    (trainFormationOutput: TrainFormationResponse): TrainGroupOnlyTrainFormation => {
      const trainFormation: TrainFormation = createTrainFormation(trainFormationOutput);
      return createTrainGroupOnlyTrainFormationFromTrainFormation(trainFormation);
    },
    _trainFormations,
  );
};

/**
 * Returns all TrainFormations of the organziation with a count of how many Run model insetances they have.
 * This is an admin tool for debugging missing Run instances on the backend
 * @param trainFormationRunCountResponses
 */
export const trainApiTrainFormationsRunCountResponseResolver: CemitApiResponseResolver<
  TrainApiTrainFormationsRoute,
  TrainFormation[]
> = ({
  data: trainFormationRunCountResponses,
}: {
  data: TrainFormationRunCountResponse[];
}): TrainFormationRunCount[] => {
  return map(
    (
      trainFormationRunCountResponse: TrainFormationRunCountResponse,
    ): TrainFormationRunCount => {
      return clsOrType<TrainFormationRunCount>(
        CemitTypename.trainFormationRunCount,
        trainFormationRunCountResponse,
      );
    },
    trainFormationRunCountResponses,
  );
};
